import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, SectionTitle } from '../../shared/components';
import { ColorChip, PriorityChip, WorkloadChip } from '../components/Chips';
import KpiTile from '../components/KpiTile';
import TimeEntryDialog from '../components/TimeEntryDialog';
import { myWorkApi, reassignmentsApi, tasksApi, timesheetApi } from '../tasks.api';
import type { MyWeek, MyWork, MyWorkTask } from '../tasks.types';
import { errorMessage, fmtDate, fmtHours, fmtPct, isoDate, today } from '../tasks.utils';

/** The employee's own day or week: capacity, plan, time logged, and the tasks to work on. */
export default function MyWorkPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? today();
  const view = (params.get('view') as 'day' | 'week') ?? 'day';
  const [day, setDay] = useState<MyWork | null>(null);
  const [week, setWeek] = useState<MyWeek | null>(null);
  const [loading, setLoading] = useState(false);
  const [logFor, setLogFor] = useState<number | null | undefined>(undefined);
  // Meetings, training and the like: their own button and dialog, apart from task time.
  const [loggingOther, setLoggingOther] = useState(false);

  const go = (patch: Record<string, string>) => setParams({ date, view, ...patch }, { replace: true });
  const shift = (n: number) => go({ date: isoDate(dayjs(date).add(n, view === 'week' ? 'week' : 'day')) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'day') setDay(await myWorkApi.day(date));
      else setWeek(await myWorkApi.week(date));
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load your work'));
    } finally {
      setLoading(false);
    }
  }, [date, view]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const tasks: MyWorkTask[] = (view === 'day' ? day?.tasks : week?.tasks) ?? [];
  const loggable = tasks
    .filter((t) => t.assignmentStatus === 'ACTIVE' || Number(t.loggedHours) > 0)
    .map((t) => ({ id: t.task.id, label: `${t.task.taskCode} · ${t.task.title}` }));

  const d = day?.day;
  // Meetings, training and the like: logged under a category rather than a task.
  const otherTime = (view === 'day' ? day?.otherTime : week?.otherTime) ?? [];

  return (
    <Stack gap={3}>
      <SectionTitle
        title={view === 'day' ? `MY WORK — ${dayjs(date).format('DD MMM YYYY').toUpperCase()}` : `MY WEEK — ${fmtDate(week?.weekStart)} to ${fmtDate(week?.weekEnd)}`}
        action={
          <Stack direction='row' gap={1} alignItems='center'>
            <ToggleButtonGroup size='small' exclusive value={view} onChange={(_, v) => v && go({ view: v })}>
              <ToggleButton value='day'>Day</ToggleButton>
              <ToggleButton value='week'>Week</ToggleButton>
            </ToggleButtonGroup>
            <IconButton aria-label='Previous' onClick={() => shift(-1)}>
              <Icon icon='mdi:chevron-left' />
            </IconButton>
            <TextField type='date' size='small' value={date} onChange={(e) => e.target.value && go({ date: e.target.value })} />
            <IconButton aria-label='Next' onClick={() => shift(1)}>
              <Icon icon='mdi:chevron-right' />
            </IconButton>
            <Button size='small' onClick={() => go({ date: today() })}>Today</Button>
            <Button size='small' variant='outlined' startIcon={<Icon icon='mdi:clock-plus-outline' />} onClick={() => setLogFor(null)}>
              Log time
            </Button>
            <Tooltip title='Log other work — a meeting, training or office work that is not a task'>
              <IconButton size='small' color='primary' aria-label='Log other work' onClick={() => setLoggingOther(true)}>
                <Icon icon='mdi:account-group-outline' />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      {loading && <LinearProgress />}

      {view === 'day' && day && (
        <>
          {day.runningTimer && (
            <Alert severity='success' icon={<Icon icon='mdi:timer-outline' />}
              action={<Button color='inherit' onClick={() => act(() => tasksApi.timer(day.runningTimer!.taskId as number, 'pause'), 'Paused')}>Pause</Button>}>
              Timer running on {day.runningTimer.taskCode} · {day.runningTimer.taskTitle} — {fmtHours(day.runningTimer.hours)} so far
            </Alert>
          )}
          {day.offers.map((o) => (
            <Alert key={o.id} severity='info'
              action={
                <Stack direction='row' gap={1}>
                  <Button color='inherit' onClick={() => act(() => reassignmentsApi.accept(o.id), 'Task accepted')}>Accept</Button>
                  <Button color='inherit' onClick={() => navigate(`/tasks/${o.taskId}`)}>Details</Button>
                </Stack>
              }>
              {o.fromEmployee?.name} is handing you {o.taskCode} · {o.taskTitle} ({fmtHours(o.transferredHours)}){o.expired ? ' — expired' : `, answer by ${fmtDate(o.expiresAt)}`}.
            </Alert>
          ))}
          {day.pendingAcceptance.map((a) => (
            <Alert key={a.id} severity='info'
              action={<Button color='inherit' onClick={() => act(() => tasksApi.accept(a.taskId), 'Task accepted')}>Accept</Button>}>
              A new task awaits your acceptance ({fmtHours(a.allocatedHours)}). <Box component='span' sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(`/tasks/${a.taskId}`)}>Open it</Box>
            </Alert>
          ))}
          <Stack direction='row' gap={2} flexWrap='wrap'>
            <KpiTile label='Capacity' value={fmtHours(d?.capacityHours)} icon='mdi:gauge' />
            <KpiTile label='Allocated' value={fmtHours(d?.allocatedHours)} icon='mdi:calendar-check' />
            <KpiTile label='Actual' value={fmtHours(d?.actualHours)} icon='mdi:clock-check-outline' />
            <KpiTile label='Remaining' value={fmtHours(d?.remainingHours)} icon='mdi:progress-clock' />
            <KpiTile label='Utilization' value={fmtPct(d?.utilization)} icon='mdi:chart-donut' alert={d?.workloadStatus === 'OVERLOADED'} />
          </Stack>
          {d && (
            <Stack direction='row' gap={1} alignItems='center'>
              <WorkloadChip status={d.workloadStatus} />
              {Number(d.leaveHours) > 0 && <Typography variant='body2' color='text.secondary'>{fmtHours(d.leaveHours)} on leave</Typography>}
              {!d.workingDay && <Typography variant='body2' color='text.secondary'>Not a working day</Typography>}
            </Stack>
          )}
        </>
      )}

      {view === 'week' && week && (
        <Card sx={{ py: 2 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell align='right'>Capacity</TableCell>
                <TableCell align='right'>Allocated</TableCell>
                <TableCell align='right'>Actual</TableCell>
                <TableCell align='right'>Remaining</TableCell>
                <TableCell align='right'>Utilization</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {week.workload.days.map((x) => (
                <TableRow key={x.date} hover sx={{ cursor: 'pointer', bgcolor: x.date === today() ? 'action.hover' : undefined }}
                  onClick={() => go({ view: 'day', date: x.date })}>
                  <TableCell>{dayjs(x.date).format('ddd DD MMM')}</TableCell>
                  <TableCell align='right'>{fmtHours(x.capacityHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(x.allocatedHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(x.actualHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(x.remainingHours)}</TableCell>
                  <TableCell align='right'>{fmtPct(x.utilization)}</TableCell>
                  <TableCell><WorkloadChip status={x.workloadStatus} /></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><b>Week</b></TableCell>
                <TableCell align='right'><b>{fmtHours(week.workload.capacityHours)}</b></TableCell>
                <TableCell align='right'><b>{fmtHours(week.workload.allocatedHours)}</b></TableCell>
                <TableCell align='right'><b>{fmtHours(week.workload.actualHours)}</b></TableCell>
                <TableCell align='right'><b>{fmtHours(week.workload.remainingHours)}</b></TableCell>
                <TableCell align='right'><b>{fmtPct(week.workload.utilization)}</b></TableCell>
                <TableCell><WorkloadChip status={week.workload.workloadStatus} /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}

      <Card title='TASKS' sx={{ py: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Project / task</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>{view === 'day' ? 'Planned today' : 'Planned this week'}</TableCell>
              <TableCell align='right'>Logged</TableCell>
              <TableCell align='right'>Allocated / Actual / Left</TableCell>
              <TableCell>Due</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((m) => {
              const t = m.task;
              const active = m.assignmentStatus === 'ACTIVE';
              return (
                <TableRow key={t.id} hover>
                  {/* The project the task is for, then the task. Opening it is the edit icon's job. */}
                  <TableCell>
                    <Typography variant='body2' fontWeight={600}>
                      {[t.projectCode, t.projectName].filter(Boolean).join(' - ')}
                      {t.projectLocation ? `, ${t.projectLocation}` : ''}
                    </Typography>
                    <Typography variant='body2'>{t.title}</Typography>
                  </TableCell>
                  <TableCell><PriorityChip code={t.priority} label={t.priorityLabel} fallbackColor={t.priorityColor} /></TableCell>
                  <TableCell><ColorChip label={t.statusLabel} color={t.statusColor} /></TableCell>
                  <TableCell align='right'>{fmtHours(m.plannedHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(m.loggedHours)}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {m.assignmentId ? `${fmtHours(m.assignmentAllocatedHours)} / ${fmtHours(m.assignmentActualHours)} / ${fmtHours(m.assignmentRemainingHours)}` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: t.overdue ? 'error.main' : undefined }}>{fmtDate(t.dueDate)}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {active && !m.timerRunning && !m.blocked && (
                      <IconButton color='success' aria-label='Start timer' onClick={() => act(() => tasksApi.timer(t.id, Number(t.actualHours) > 0 ? 'resume' : 'start'), 'Timer started')}>
                        <Icon icon='mdi:play' />
                      </IconButton>
                    )}
                    {/* Running, the timer only pauses here; stopping is not offered. */}
                    {m.timerRunning && (
                      <IconButton color='warning' aria-label='Pause timer' onClick={() => act(() => tasksApi.timer(t.id, 'pause'), 'Paused')}><Icon icon='mdi:pause' /></IconButton>
                    )}
                    {active && (
                      <Tooltip title='Log time'>
                        <IconButton aria-label='Log time' onClick={() => setLogFor(t.id)}><Icon icon='mdi:clock-plus-outline' /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title='Edit task'>
                      <IconButton aria-label='Edit task' onClick={() => navigate(`/tasks/${t.id}`)}><Icon icon='mdi:pencil-outline' /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {tasks.length === 0 && !loading && (
              <TableRow><TableCell {...({ colSpan: 8 } as object)} align='center' sx={{ py: 4, color: 'text.secondary' }}>Nothing planned for this {view}.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {otherTime.length > 0 && (
        <Card title='OTHER WORK' sx={{ py: 2 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {view === 'week' && <TableCell>Date</TableCell>}
                <TableCell>Category</TableCell>
                <TableCell>From – to</TableCell>
                <TableCell align='right'>Hours</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {otherTime.map((e) => (
                <TableRow key={e.id}>
                  {view === 'week' && <TableCell>{dayjs(e.workDate).format('ddd DD MMM')}</TableCell>}
                  <TableCell>{e.categoryName}</TableCell>
                  <TableCell>
                    {e.startTime ? dayjs(e.startTime).format('HH:mm') : '—'} – {e.endTime ? dayjs(e.endTime).format('HH:mm') : '—'}
                  </TableCell>
                  <TableCell align='right'>{fmtHours(e.hours)}</TableCell>
                  <TableCell>{e.notes}</TableCell>
                  <TableCell align='right'>
                    <IconButton size='small' aria-label='Delete entry' onClick={() => act(() => timesheetApi.removeEntry(e.id), 'Entry deleted')}>
                      <Icon icon='mdi:delete-outline' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <TimeEntryDialog open={loggingOther} tasks={[]} target='other' date={date > today() ? today() : date}
        onClose={() => setLoggingOther(false)} onDone={() => { setLoggingOther(false); load(); }} />
      <TimeEntryDialog open={logFor !== undefined} tasks={loggable} taskId={logFor ?? null} date={date > today() ? today() : date}
        onClose={() => setLogFor(undefined)} onDone={() => { setLogFor(undefined); load(); }} />
    </Stack>
  );
}
